import { Injectable, OnDestroy, provideEnvironmentInitializer, inject } from '@angular/core';
import { RefreshAuthRoleGQL, Role } from '@generated/graphql';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DebugableService } from '../debug/debug.type';
import { HttpClient } from '@angular/common/http';
import { fluxLogger } from '../logger/logger.instance';

const logger = fluxLogger.extend('refresh-auth');

@Injectable({
  providedIn: 'root',
})
export class RefreshAuthService implements DebugableService, OnDestroy {
  public static FLUX_ROUTE_REGEX =
    /#\/fluxo\/(?<pipeId>[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[a-f0-9]{4}-[a-f0-9]{12})/;

  public static DATABASE_ROUTE_REGEX =
    /#\/banco-de-dados\/(?<databaseId>[a-f0-9]{8}-[a-f0-9]{4}-7[a-f0-9]{3}-[a-f0-9]{4}-[a-f0-9]{12})/;

  private REFRESH_ROLE_INTERVAL = 1000 * 60 * 5; // 5 minutes

  private intervalRef: null | unknown = null;

  private lastFetch: { time: number; result: unknown; error: unknown } | null = null;

  constructor(
    private readonly refreshAuthRoleGQL: RefreshAuthRoleGQL,
    private readonly httpService: HttpClient,
    private readonly authService: AuthService,
  ) {
    this.authService.authenticatedUser$.pipe(takeUntilDestroyed()).subscribe((value) => {
      if (!value) {
        clearInterval(this.intervalRef as number);
        this.intervalRef = null;
        logger.info('User logged out, clearInterval');
        return;
      }

      if (!this.intervalRef) {
        this.initInterval();
        logger.info('User logged in, initInterval');
      }
    });
  }

  public initInterval() {
    this.refreshWikiCookie();
    this.intervalRef = setInterval(() => {
      this.refreshAuthenticatedUser().catch((error) => {
        logger.error('Error during periodic refresh of authenticated user', error);
      });
      this.refreshWikiCookie().catch((error) => {
        logger.error('Error during periodic refresh of wiki cookie', error);
      });
    }, this.REFRESH_ROLE_INTERVAL);
  }

  public async refreshAuthenticatedUser() {
    if (!this.authService.isAuthenticated) {
      return;
    }

    let pipeId: string | null = null;
    let databaseId: string | null = null;

    const isOnFluxRoute = window.location.toString().match(RefreshAuthService.FLUX_ROUTE_REGEX);
    const isOnDatabaseRoute = window.location
      .toString()
      .match(RefreshAuthService.DATABASE_ROUTE_REGEX);

    if (isOnFluxRoute) {
      pipeId = isOnFluxRoute.groups?.['pipeId'] ?? null;
    }

    if (isOnDatabaseRoute) {
      databaseId = isOnDatabaseRoute.groups?.['databaseId'] ?? null;
    }

    try {
      const result = await firstValueFrom(
        this.refreshAuthRoleGQL.fetch({
          roleEntityContext: {
            pipeId: pipeId ?? undefined,
            databaseId: databaseId ?? undefined,
          },
        }),
      ).then(({ data }) => data?.refreshAuthRole);

      if (!result?.globalRole) {
        return;
      }

      this.authService.setAuthenticatedUser({
        ...this.authService.authenticatedUser(),
        role: result.globalRole as Role,
      });

      if (pipeId && result.pipeRole) {
        this.authService.setPipeRole(pipeId, result.pipeRole);
      }

      if (databaseId && result.databaseRole) {
        this.authService.setDatabaseRole(databaseId, result.databaseRole);
      }

      this.lastFetch = {
        time: Date.now(),
        result: result,
        error: null,
      };
    } catch (error) {
      logger.error('Error refreshing authenticated user', error);
      this.lastFetch = {
        time: Date.now(),
        result: null,
        error: error,
      };
    }
  }

  public async refreshWikiCookie() {
    if (!this.authService.isAuthenticated) {
      return;
    }

    try {
      await firstValueFrom(this.httpService.get('/api/wiki/cookie'));
    } catch (error) {
      logger.error('Error refreshing wiki cookie', error);
    }
  }

  public generateDebugReport() {
    return {
      isOnFluxRoute: window.location.toString().match(RefreshAuthService.FLUX_ROUTE_REGEX),
      hasInterval: Boolean(this.intervalRef),
      lastFetch: this.lastFetch,
    };
  }

  static provider() {
    return provideEnvironmentInitializer(() => {
      const refreshAuthService = inject(RefreshAuthService);
      refreshAuthService.refreshAuthenticatedUser();
      refreshAuthService.refreshWikiCookie();
    });
  }

  ngOnDestroy(): void {
    if (this.intervalRef) {
      clearInterval(this.intervalRef as number);
      this.intervalRef = null;
    }
    this.lastFetch = null;
  }
}
